import { useState, useEffect } from "react";
import { apiClient } from "@/services/apiClient";
import type { PlatformMetrics } from "@/components/PostCard";
import type { Post } from "@/core/types/postTypes";
import { proRataBaseline, proRataFor, type ProRata } from "../utils/metricChange";

// --- Platform post types ---

interface PostMetricsFacebook {
  reactions: number;
  comments: number;
  shares: number;
}

interface PostMetricsInstagram {
  likes: number;
  comments: number;
  saved: number;
}

interface InternalPostData {
  postId: string;
  postType: string;
  groupName: string | null;
  /** Internal-source caption (what we stored in our DB). Often diverges
   *  from the platform caption because publishers append localized
   *  hashtags at publish time. */
  caption: string | null;
  semanticTags: {
    contentType?: string;
    category?: string;
    subcategory?: string;
    visualStyle?: string[];
    presentation?: string;
  } | null;
}

export interface PlatformPost {
  id: string;
  message?: string;
  caption?: string;
  mediaType?: string;
  imageUrl?: string;
  mediaUrl?: string;
  thumbnailUrl?: string;
  permalink?: string;
  /** When the post went out, as the Graph API sends it
   *  ("2026-10-05T09:30:00+0000"): Facebook's created_time... */
  createdTime?: string;
  /** ...and Instagram's timestamp. */
  timestamp?: string;
  metrics: PostMetricsFacebook | PostMetricsInstagram;
  internal?: InternalPostData;
}

// --- Report types ---

interface FacebookPageInsights {
  pageId: string;
  pageName: string;
  /** Keyed by Meta metric name. The API boundary camelizes every dict
   *  key, so these arrive camelCased (e.g. `pageImpressions`,
   *  `pageFanAdds`), not the raw snake_case Graph metric names. */
  insights: Record<string, unknown>;
}

interface InstagramAccountInsights {
  instagramId: string;
  username: string;
  insights: Record<string, unknown>;
}

interface ContentInsightByType {
  postType: string;
  count: number;
  avgEngagement: number;
  totalEngagement: number;
}

interface ContentInsightByCategory {
  category: string;
  count: number;
  avgEngagement: number;
}

export interface ContentInsights {
  byPostType: ContentInsightByType[];
  byCategory: ContentInsightByCategory[];
  totalMatched: number;
}

export interface ReportMeta {
  facebookFetchedAt: string | null;
  instagramFetchedAt: string | null;
  tiktokFetchedAt: string | null;
  updatedAt: string | null;
}

export interface SocialMediaReport {
  clientId: string;
  year: number;
  month: number;
  period: {
    start: string;
    end: string;
  };
  facebook: {
    pages: FacebookPageInsights[];
    totalPosts: number;
    totalImpressions: number;
    totalReach: number;
    totalFans: number;
    totalFanAdds: number;
    totalFanRemoves: number;
    totalEngagedUsers: number;
    // v24 page-level additions. Older cached blobs lack these; treat
    // missing as 0 in components.
    totalPostsImpressions?: number;
    totalPostsImpressionsUnique?: number;
    totalOrganicImpressions?: number;
    totalPaidImpressions?: number;
    totalViralImpressions?: number;
    totalViralReach?: number;
    totalProfileViews?: number;
    totalPostEngagements?: number;
    totalReactions?: number;
    totalReactionsLike?: number;
    totalReactionsLove?: number;
    totalReactionsWow?: number;
    totalReactionsHaha?: number;
    totalReactionsSorry?: number;
    totalReactionsAnger?: number;
    totalVideoViews?: number;
    totalVideoViewsUnique?: number;
    totalVideoViewsOrganic?: number;
    totalVideoViewsPaid?: number;
    totalVideoCompleteViews30s?: number;
    allPosts: PlatformPost[];
  } | null;
  instagram: {
    accounts: InstagramAccountInsights[];
    totalPosts: number;
    totalImpressions: number;
    totalReach: number;
    totalProfileViews: number;
    totalFollowers: number;
    totalEngagement: number;
    totalInteractions: number;
    totalLikes: number;
    totalComments: number;
    totalShares: number;
    totalSaved: number;
    totalWebsiteClicks: number;
    totalProfileLinksTaps: number;
    totalReplies?: number;
    followerGrowth: number;
    allPosts: PlatformPost[];
  } | null;
  tiktok: {
    accounts: Array<{
      openId: string;
      displayName: string;
      username: string;
      followerCount: number;
      followingCount: number;
      likesCount: number;
      videoCount: number;
    }>;
    totalFollowers: number;
    totalFollowing: number;
    totalLikes: number;
    totalVideos: number;
  } | null;
  combined: {
    totalPosts: number;
    totalImpressions: number;
    totalReach: number;
    totalEngagement: number;
  };
  topPosts: TopPost[];
  contentInsights?: ContentInsights;
  _meta?: ReportMeta;
}

/** Top-post entry, grouped by internal post. A single card on the
 *  reporting page represents one internal post and shows per-platform
 *  metrics inside (FB + IG merged into one card, not two). */
export interface TopPost {
  /** Internal Post.id, or null when the platform post couldn't be
   *  matched to an internal record (rare — published outside the app). */
  postId: string | null;
  /** Enrichment payload from the backend's internal-data matcher. */
  internal: InternalPostData | null;
  /** Per-platform breakdown (permalinks + per-platform engagement
   *  metrics). Used by the card to surface "view on FB / IG" links. */
  platforms: Partial<Record<string, TopPostPlatformEntry>>;
  /** Sum of per-platform engagement deltas. Drives top-N ranking. */
  engagementScore: number;
  /** Full internal Post dict hydrated at request time by the report
   *  route. Carries media_items + lifetime metrics_by_platform so the
   *  reporting card can render with the same EditorPostCard that
   *  powers /posts. Null when no internal match exists. */
  post: Post | null;
}

export interface TopPostPlatformEntry {
  /** Original platform post payload — used for the permalink and as
   *  a fallback when there's no matched internal Post. */
  post: PlatformPost;
  /** Per-platform metrics for this report period. Same shape as the
   *  posts-view PublishedPostStats so the component can render either
   *  data source without branching. */
  metrics: PlatformMetrics;
}

export interface ReportComparison {
  current: SocialMediaReport;
  previous: SocialMediaReport | null;
  changes: {
    combined: {
      posts: { value: number; percentage: number };
      impressions: { value: number; percentage: number };
      /** Null on a pro-rata month: a unique count does not scale. */
      reach: { value: number; percentage: number } | null;
      engagement: { value: number; percentage: number };
    };
    facebook: {
      posts: { value: number; percentage: number };
      impressions: { value: number; percentage: number };
      /** Null on a pro-rata month: a unique count does not scale. */
      reach: { value: number; percentage: number } | null;
      fans: { value: number; percentage: number };
    } | null;
    instagram: {
      posts: { value: number; percentage: number };
      impressions: { value: number; percentage: number };
      /** Null on a pro-rata month: a unique count does not scale. */
      reach: { value: number; percentage: number } | null;
      followers: { value: number; percentage: number };
    } | null;
  };
  /** Set when the report covers the month only in part: its flows were set
   *  against this share of the month before. */
  proRata: ProRata | null;
}

interface PlatformFollowerCounts {
  facebook: number;
  instagram: number;
  tiktok: number;
}

interface FollowerChanges {
  facebook: { gained: number; lost: number };
  instagram: { growth: number };
}

export interface TrendDataPoint {
  year: number;
  month: number;
  totalPosts: number;
  totalImpressions: number;
  totalReach: number;
  totalEngagement: number;
  followers?: PlatformFollowerCounts;
  followerChanges?: FollowerChanges;
  /** IG signals that map closest to actual booking intent. The agency
   *  optimises for this line trending up. */
  instagramConversion?: {
    profileLinksTaps: number;
    websiteClicks: number;
    profileViews: number;
  };
  /** Paid link_click per month from month-bounded ad windows (TD-009).
   *  null = no ad window covers the month — the chart draws a gap,
   *  never a fabricated zero (DD-021). */
  paidConversion?: {
    linkClicks: number;
  } | null;
  /** FB paid vs organic split. Useful for separating boosted-ad spend
   *  from the agency's organic contribution when chatting to clients. */
  facebookBreakdown?: {
    organicImpressions: number;
    paidImpressions: number;
    viralImpressions: number;
    profileViews: number;
    postEngagements: number;
  };
}

const calculateChange = (current: number, previous: number) => {
  const value = current - previous;
  const percentage = previous > 0 ? (value / previous) * 100 : 0;
  return { value, percentage };
};

const buildComparison = (
  current: SocialMediaReport,
  previous: SocialMediaReport
): ReportComparison => {
  // A month the report covers only in part sets each flow against the same
  // share of the month before; follower counts are levels and stay whole.
  // Reach may be a unique count, which does not scale, so it has no change.
  const proRata = proRataFor(current.period?.end, current.year, current.month);
  const flow = (cur: number, prev: number) =>
    calculateChange(cur, proRata ? proRataBaseline(prev, proRata.share) : prev);
  const reach = (cur: number, prev: number) => (proRata ? null : calculateChange(cur, prev));
  const changes: ReportComparison["changes"] = {
    combined: {
      posts: flow(current.combined.totalPosts, previous.combined.totalPosts),
      impressions: flow(current.combined.totalImpressions, previous.combined.totalImpressions),
      reach: reach(current.combined.totalReach, previous.combined.totalReach),
      engagement: flow(current.combined.totalEngagement, previous.combined.totalEngagement),
    },
    facebook: null,
    instagram: null,
  };

  if (current.facebook && previous.facebook) {
    changes.facebook = {
      posts: flow(current.facebook.totalPosts, previous.facebook.totalPosts),
      impressions: flow(current.facebook.totalImpressions, previous.facebook.totalImpressions),
      reach: reach(current.facebook.totalReach, previous.facebook.totalReach),
      fans: calculateChange(current.facebook.totalFans, previous.facebook.totalFans),
    };
  }

  if (current.instagram && previous.instagram) {
    changes.instagram = {
      posts: flow(current.instagram.totalPosts, previous.instagram.totalPosts),
      impressions: flow(current.instagram.totalImpressions, previous.instagram.totalImpressions),
      reach: reach(current.instagram.totalReach, previous.instagram.totalReach),
      followers: calculateChange(current.instagram.totalFollowers, previous.instagram.totalFollowers),
    };
  }

  return { current, previous, changes, proRata };
};

export const useSocialMediaReport = (
  clientId: string,
  year: number,
  month: number
) => {
  const [report, setReport] = useState<SocialMediaReport | null>(null);
  const [previousReport, setPreviousReport] = useState<SocialMediaReport | null>(null);
  const [comparison, setComparison] = useState<ReportComparison | null>(null);
  const [historicalData, setHistoricalData] = useState<TrendDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshingPlatform, setRefreshingPlatform] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) {
      setReport(null);
      setPreviousReport(null);
      setComparison(null);
      setHistoricalData([]);
      setIsPending(false);
      return;
    }

    const fetchReports = async () => {
      setIsLoading(true);
      setError(null);
      setIsPending(false);

      try {
        let prevYear = year;
        let prevMonth = month - 1;
        if (prevMonth < 1) {
          prevMonth = 12;
          prevYear = year - 1;
        }

        const [currentData, previousData, trendsData] = await Promise.all([
          apiClient.get(`/api/social-media/reports/${clientId}`, {
            params: { year, month },
          }).catch((err: unknown) => {
            // apiClient interceptor converts 404s to plain Error,
            // stripping the response metadata. Match on message instead.
            if (err instanceof Error && err.message === "Report not yet generated") {
              return { __pending: true };
            }
            throw err;
          }),
          apiClient.get(`/api/social-media/reports/${clientId}`, {
            params: { year: prevYear, month: prevMonth },
          }).catch(() => null),
          apiClient.get(`/api/social-media/trends/${clientId}`, {
            params: { year, month, months: 6 },
          }).catch(() => []),
        ]);

        // Handle pending state
        if (currentData && typeof currentData === "object" && "__pending" in currentData) {
          setIsPending(true);
          setReport(null);
          setPreviousReport(null);
          setComparison(null);
          setHistoricalData(trendsData as TrendDataPoint[]);
          return;
        }

        const current = currentData as SocialMediaReport;
        const previous = previousData as SocialMediaReport | null;
        const trends = trendsData as TrendDataPoint[];

        setReport(current);
        setPreviousReport(previous);
        setHistoricalData(trends);

        if (previous) {
          setComparison(buildComparison(current, previous));
        } else {
          setComparison(null);
        }
      } catch (err: unknown) {
        const apiErr = err as { response?: { data?: { error?: string } }; message?: string };
        const errorMessage =
          apiErr.response?.data?.error ||
          apiErr.message ||
          "Failed to load report";
        setError(errorMessage);
        setReport(null);
        setPreviousReport(null);
        setComparison(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, [clientId, year, month]);

  const refreshReport = async () => {
    if (!clientId) return;

    setIsRefreshing(true);
    try {
      // Kicks off background generation (returns 202)
      await apiClient.post(
        `/api/social-media/reports/${clientId}/generate`,
        { year, month }
      );

      // Also kick off previous month in background
      let prevYear = year;
      let prevMonth = month - 1;
      if (prevMonth < 1) {
        prevMonth = 12;
        prevYear = year - 1;
      }

      await apiClient.post(
        `/api/social-media/reports/${clientId}/generate`,
        { year: prevYear, month: prevMonth }
      ).catch(() => null);

      // Poll the GET endpoint until the report is ready
      const maxAttempts = 30;
      const pollInterval = 3000;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        try {
          const [currentData, previousData, trendsData] = await Promise.all([
            apiClient.get(`/api/social-media/reports/${clientId}`, {
              params: { year, month },
            }),
            apiClient.get(`/api/social-media/reports/${clientId}`, {
              params: { year: prevYear, month: prevMonth },
            }).catch(() => null),
            apiClient.get(`/api/social-media/trends/${clientId}`, {
              params: { year, month, months: 6 },
            }).catch(() => []),
          ]);

          const current = currentData as SocialMediaReport;
          const meta = current._meta;
          // Check if the report was recently updated (within last 60s)
          if (meta?.updatedAt) {
            const updatedAt = new Date(meta.updatedAt).getTime();
            const now = Date.now();
            if (now - updatedAt < 90000) {
              setReport(current);
              setIsPending(false);
              setError(null);

              const previous = previousData as SocialMediaReport | null;
              const trends = trendsData as TrendDataPoint[];
              setPreviousReport(previous);
              setHistoricalData(trends);

              if (previous) {
                setComparison(buildComparison(current, previous));
              } else {
                setComparison(null);
              }
              break;
            }
          }
        } catch {
          // Report not ready yet, keep polling
        }
      }
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage =
        apiErr.response?.data?.error ||
        apiErr.message ||
        "Failed to refresh report";
      setError(errorMessage);
    } finally {
      setIsRefreshing(false);
    }
  };

  const refreshPlatform = async (platform: "facebook" | "instagram" | "tiktok") => {
    if (!clientId || refreshingPlatform) return;

    setRefreshingPlatform(platform);
    try {
      // Kicks off background generation (returns 202)
      await apiClient.post(
        `/api/social-media/reports/${clientId}/generate`,
        { year, month, platform }
      );

      // Poll until report is updated
      const maxAttempts = 20;
      const pollInterval = 3000;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        try {
          const freshData = await apiClient.get(
            `/api/social-media/reports/${clientId}`,
            { params: { year, month } }
          );
          const current = freshData as SocialMediaReport;
          const meta = current._meta;
          const fetchedAtKeyMap: Record<string, keyof ReportMeta> = {
            facebook: "facebookFetchedAt",
            instagram: "instagramFetchedAt",
            tiktok: "tiktokFetchedAt",
          };
          const fetchedAtKey = fetchedAtKeyMap[platform];
          if (meta?.[fetchedAtKey]) {
            const fetchedAt = new Date(meta[fetchedAtKey] as string).getTime();
            const now = Date.now();
            if (now - fetchedAt < 90000) {
              setReport(current);
              setError(null);
              if (previousReport) {
                setComparison(buildComparison(current, previousReport));
              }
              break;
            }
          }
        } catch {
          // Not ready yet
        }
      }
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage =
        apiErr.response?.data?.error ||
        apiErr.message ||
        `Failed to refresh ${platform}`;
      setError(errorMessage);
    } finally {
      setRefreshingPlatform(null);
    }
  };

  return {
    report,
    previousReport,
    comparison,
    historicalData,
    isLoading,
    error,
    isPending,
    isRefreshing,
    refreshingPlatform,
    refreshReport,
    refreshPlatform,
  };
};
